import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Menu, X, Play } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { isDemoMode } from './DemoBanner';
import NotificationBell from './NotificationBell';
import TrialBanner from './TrialBanner';
import DesktopNavLinks from './navbar/DesktopNavLinks';
import ContextIndicators from './navbar/ContextIndicators';
import UserMenu from './navbar/UserMenu';
import MobileMenu from './navbar/MobileMenu';
import ChangePasswordDialog from './navbar/ChangePasswordDialog';
import { DEMO_ROLE_EMAILS } from './navbar/navConstants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Top navigation orchestrator.
 *
 * Holds shared state (companies, operator tenants, provider orgs, dialog state)
 * and composes the visual pieces:
 *   • DesktopNavLinks       — primary nav, Company Entries dropdown, org switcher
 *   • ContextIndicators     — operator/provider/demo pills + role badge
 *   • UserMenu              — avatar trigger + role-branched user dropdown
 *   • MobileMenu            — hamburger slide-down
 *   • ChangePasswordDialog  — password change modal
 */
const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user, logout, hasRole, allTenants, selectedTenants, setSelectedTenants,
    userOrgs, activeOrgId, setActiveOrgId,
    operatorContext, setOperatorContext, resetOperatorContext,
    canAccessOrg, isUnscoped, tryDemo, demoViewMode, checkAuth,
  } = useAuth();

  // Demo role-switcher: only when logged in as one of the seeded demo users in demo mode
  const isDemoRoleUser = isDemoMode() && user && DEMO_ROLE_EMAILS.includes(user.email);
  const [switchingRole, setSwitchingRole] = useState(false);

  const handleDemoRoleSwitch = useCallback(async (role) => {
    if (!isDemoRoleUser || switchingRole || role === user.role) return;
    setSwitchingRole(true);
    try {
      await tryDemo({ view: demoViewMode || user.account_type || 'provider', role });
      navigate('/', { replace: true });
      window.location.reload();
    } catch {
      toast.error('Could not switch role. Please try again.');
      setSwitchingRole(false);
    }
  }, [isDemoRoleUser, switchingRole, user?.role, user?.account_type, demoViewMode, tryDemo, navigate]);

  // Scoped Provider users see only orgs they have access to
  const visibleOrgs = isUnscoped ? userOrgs : userOrgs.filter(o => canAccessOrg(o.org_id));

  // If a previously-selected active org becomes inaccessible, drop back to "All Orgs"
  useEffect(() => {
    if (!isUnscoped && activeOrgId && !canAccessOrg(activeOrgId)) {
      setActiveOrgId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnscoped, activeOrgId, user?.org_access]);

  const [companies, setCompanies] = useState([]);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [supportCount, setSupportCount] = useState(0);
  const [operatorTenants, setOperatorTenants] = useState([]);
  const [mobileCompaniesOpen, setMobileCompaniesOpen] = useState(false);
  const [providerOrgs, setProviderOrgs] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [trialDays, setTrialDays] = useState(null);
  const closeTimerRef = useRef(null);

  const inContext = user?.is_platform_operator && operatorContext?.tenantId;
  const hasAccountType = user?.is_platform_operator && operatorContext?.accountType;
  const isProviderContext = inContext && operatorContext?.accountType === 'provider';
  const isInOrgView = user?.account_type === 'provider' && !!activeOrgId;

  const startCloseTimer = useCallback(() => {
    closeTimerRef.current = setTimeout(() => setMenuOpen(false), 1500);
  }, []);
  const cancelCloseTimer = useCallback(() => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
  }, []);
  useEffect(() => () => cancelCloseTimer(), [cancelCloseTimer]);

  // ── Companies dropdown source ────────────────────────────────────────────
  // Refetches when the active org changes too — without `activeOrgId` in the
  // deps, switching org in the nav-bar dropdown left the "Company Entries"
  // menu showing the previous org's companies.
  useEffect(() => {
    if (user?.role === 'super_admin' && selectedTenants.length === 0 && !inContext) {
      setCompanies([]);
      return;
    }
    const fetchCompanies = async () => {
      try {
        const response = await axios.get(`${API}/companies`, { withCredentials: true });
        setCompanies(response.data);
      } catch (err) {
        console.error('Error fetching companies:', err);
      }
    };
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenants, operatorContext?.tenantId, activeOrgId]);

  // ── Support queue count (operator only)
  useEffect(() => {
    if (!user?.is_platform_operator) return;
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${API}/support/queue/count`, { withCredentials: true });
        setSupportCount(res.data.count || 0);
      } catch (err) { console.warn('[Navbar] support count fetch failed', err); }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [user?.is_platform_operator]);

  // ── Operator tenants list for "Select Organization"
  useEffect(() => {
    if (!user?.is_platform_operator || !operatorContext?.accountType) {
      setOperatorTenants([]); return;
    }
    axios.get(`${API}/operator/tenants?account_type=${operatorContext.accountType}`, { withCredentials: true })
      .then(res => setOperatorTenants(res.data))
      .catch((err) => console.warn('[Navbar] operator tenants fetch failed', err));
  }, [user?.is_platform_operator, operatorContext?.accountType]);

  // ── Provider context: list of orgs in the V2 dropdown
  useEffect(() => {
    if (!isProviderContext) { setProviderOrgs([]); return; }
    axios.get(`${API}/operator/tenants/${operatorContext.tenantId}/organizations`, { withCredentials: true })
      .then(res => setProviderOrgs(res.data))
      .catch(() => setProviderOrgs([]));
  }, [isProviderContext, operatorContext?.tenantId]);

  // ── Subscription/trial pill
  useEffect(() => {
    if (!user || user.is_platform_operator) return;
    axios.get(`${API}/billing/subscription`, { withCredentials: true })
      .then(res => {
        if (res.data.trial_info?.is_trial) setTrialDays(res.data.trial_info.days_remaining);
      })
      .catch((err) => console.warn('[Navbar] subscription fetch failed', err));
  }, [user]);

  // Close mobile menu on route change
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  // ── Action handlers ─────────────────────────────────────────────────────
  const isActive = (path) => location.pathname === path;
  const handleCompanySelect = (companyName) => navigate('/companies', { state: { selectedCompany: companyName } });
  const handleLogout = async () => { await logout(); navigate('/login'); };

  const selectOperatorOrg = (tenant) => {
    setOperatorContext({ accountType: operatorContext.accountType, tenantId: tenant.tenant_id, tenantName: tenant.name, orgId: null, orgName: null });
    setMenuOpen(false);
    navigate('/');
  };
  const selectProviderOrg = (org) => {
    setOperatorContext({ ...operatorContext, orgId: org.org_id, orgName: org.name });
    navigate('/');
  };
  const clearProviderOrg = () => {
    setOperatorContext({ ...operatorContext, orgId: null, orgName: null });
    navigate('/');
  };
  const handleResetContext = () => {
    setMenuOpen(false);
    resetOperatorContext();
    navigate('/');
  };
  const selectOrg = (tenantId) => {
    const isDeselect = selectedTenants.includes(tenantId);
    setSelectedTenants((prev) => prev.includes(tenantId) ? [] : [tenantId]);
    if (!isDeselect) {
      const orgName = allTenants.find(t => t.tenant_id === tenantId)?.name;
      toast.success(`Switched to ${orgName}`);
    }
    setMenuOpen(false);
  };
  const getOrgLabel = () => {
    if (selectedTenants.length === 1) {
      const t = allTenants.find((t) => t.tenant_id === selectedTenants[0]);
      return t?.name || 'Select Organization';
    }
    return 'Select Organization';
  };
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    const setMode = user?.has_local_password === false;
    setPasswordSaving(true);
    try {
      const payload = setMode
        ? { new_password: newPassword }
        : { current_password: currentPassword, new_password: newPassword };
      await axios.put(`${API}/auth/change-password`, payload, { withCredentials: true });
      toast.success(setMode ? 'Password set successfully' : 'Password changed successfully');
      setShowPasswordDialog(false);
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      // Refresh auth context so `has_local_password` flips to true and the
      // menu re-labels to "Change Password" on next open.
      if (typeof checkAuth === 'function') { checkAuth(); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setPasswordSaving(false); }
  };

  return (
    <>
      <nav className="bg-gray-100 border-b border-gray-200 px-4 md:px-6 py-3 sticky top-0 z-50" data-testid="navbar">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 md:gap-6">
            <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
              <img src="/logo.png" alt="Logo" className="w-6 h-6" />
              <span className="font-semibold text-gray-700">CMS</span>
            </Link>

            <DesktopNavLinks
              isActive={isActive} hasRole={hasRole} user={user} inContext={inContext}
              companies={companies} handleCompanySelect={handleCompanySelect}
              visibleOrgs={visibleOrgs} activeOrgId={activeOrgId} setActiveOrgId={setActiveOrgId}
            />
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {user && (
              <div className="hidden md:flex items-center gap-3">
                <ContextIndicators
                  user={user} inContext={inContext} isProviderContext={isProviderContext}
                  operatorContext={operatorContext} handleResetContext={handleResetContext}
                  providerOrgs={providerOrgs} selectProviderOrg={selectProviderOrg} clearProviderOrg={clearProviderOrg}
                  isDemoRoleUser={isDemoRoleUser} switchingRole={switchingRole} handleDemoRoleSwitch={handleDemoRoleSwitch}
                />
                <NotificationBell />
                <UserMenu
                  user={user} hasRole={hasRole} navigate={navigate} handleLogout={handleLogout}
                  menuOpen={menuOpen} setMenuOpen={setMenuOpen}
                  startCloseTimer={startCloseTimer} cancelCloseTimer={cancelCloseTimer}
                  inContext={inContext} hasAccountType={hasAccountType} isInOrgView={isInOrgView}
                  operatorContext={operatorContext} handleResetContext={handleResetContext}
                  visibleOrgs={visibleOrgs} activeOrgId={activeOrgId} setActiveOrgId={setActiveOrgId}
                  operatorTenants={operatorTenants} selectOperatorOrg={selectOperatorOrg}
                  allTenants={allTenants} selectedTenants={selectedTenants}
                  selectOrg={selectOrg} getOrgLabel={getOrgLabel}
                  setShowPasswordDialog={setShowPasswordDialog} supportCount={supportCount} trialDays={trialDays}
                />
              </div>
            )}

            {/* Demo Tour button - mobile only */}
            {user && isDemoMode() && (
              <button
                onClick={() => window.dispatchEvent(new Event('start-demo-tour'))}
                className="md:hidden flex items-center gap-1.5 bg-gray-900 hover:bg-gray-800 text-white text-[11px] font-medium px-2.5 py-1.5 rounded-full transition-colors"
                data-testid="mobile-demo-tour-btn"
              >
                <Play className="w-3 h-3" />
                Demo Tour
              </button>
            )}

            {/* Notification bell - mobile */}
            {user && (
              <div className="md:hidden">
                <NotificationBell />
              </div>
            )}

            {/* Hamburger menu - mobile */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded hover:bg-gray-200 transition-colors"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5 text-gray-700" /> : <Menu className="w-5 h-5 text-gray-700" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <MobileMenu
            user={user} hasRole={hasRole} isActive={isActive} navigate={navigate} handleLogout={handleLogout}
            setMobileMenuOpen={setMobileMenuOpen} setShowPasswordDialog={setShowPasswordDialog}
            inContext={inContext} isInOrgView={isInOrgView}
            operatorContext={operatorContext} handleResetContext={handleResetContext}
            visibleOrgs={visibleOrgs} activeOrgId={activeOrgId} setActiveOrgId={setActiveOrgId}
            companies={companies} handleCompanySelect={handleCompanySelect}
            mobileCompaniesOpen={mobileCompaniesOpen} setMobileCompaniesOpen={setMobileCompaniesOpen}
            trialDays={trialDays}
          />
        )}
      </nav>

      <TrialBanner />

      <ChangePasswordDialog
        open={showPasswordDialog} onOpenChange={setShowPasswordDialog}
        setMode={user?.has_local_password === false}
        currentPassword={currentPassword} setCurrentPassword={setCurrentPassword}
        newPassword={newPassword} setNewPassword={setNewPassword}
        confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword}
        passwordSaving={passwordSaving} onSubmit={handlePasswordChange}
      />
    </>
  );
};

export default Navbar;
